// TerrainBlending.js
// This file contains shader code for blending terrain textures based on height, slope, and biome with shadow support

/**
 * Terrain blending shader constants and utility functions
 */
export default {
  // Vertex shader for terrain with multi-texture support and shadows
  vertexShader: `
    // Attributes from geometry
    varying vec2 vUv;
    varying vec3 vPosition;
    varying vec3 vNormal;
    
    // Height and slope data for fragment shader
    varying float vHeight;
    varying float vSlope;
    
    // Shadow mapping variables
    #ifdef USE_SHADOWMAP
      #if NUM_DIR_LIGHT_SHADOWS > 0
        uniform mat4 directionalShadowMatrix[ NUM_DIR_LIGHT_SHADOWS ];
        varying vec4 vDirectionalShadowCoord[ NUM_DIR_LIGHT_SHADOWS ];
      #endif
    #endif
    
    void main() {
      // Set varyings
      vUv = uv;
      vPosition = position;
      vNormal = normalize(normal);
      
      // Calculate world position for the fragment shader
      vec4 worldPosition = modelMatrix * vec4(position, 1.0);
      
      // Calculate height (normalized Z position)
      vHeight = position.z;
      
      // Calculate slope (dot product between normal and up vector)
      vSlope = 1.0 - dot(vNormal, vec3(0.0, 1.0, 0.0));
      
      // Shadow mapping code
      #ifdef USE_SHADOWMAP
        #if NUM_DIR_LIGHT_SHADOWS > 0
          #pragma unroll_loop_start
          for ( int i = 0; i < NUM_DIR_LIGHT_SHADOWS; i ++ ) {
            vDirectionalShadowCoord[ i ] = directionalShadowMatrix[ i ] * worldPosition;
          }
          #pragma unroll_loop_end
        #endif
      #endif
      
      // Standard vertex transformation
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  
  // Fragment shader for terrain with multi-texture support and shadows
  fragmentShader: `
    // Three.js shadow mapping includes
    #include <packing>
    
    // Shadow map uniforms
    #ifdef USE_SHADOWMAP
      #if NUM_DIR_LIGHT_SHADOWS > 0
        uniform sampler2D directionalShadowMap[ NUM_DIR_LIGHT_SHADOWS ];
        varying vec4 vDirectionalShadowCoord[ NUM_DIR_LIGHT_SHADOWS ];
      #endif
      
      uniform vec2 shadowMapSize[ NUM_DIR_LIGHT_SHADOWS ];
      uniform float shadowBias[ NUM_DIR_LIGHT_SHADOWS ];
      uniform float shadowRadius[ NUM_DIR_LIGHT_SHADOWS ];
    #endif
    
    // Texture uniforms
    uniform sampler2D sandTexture;
    uniform sampler2D grassTexture;
    
    // Control texture for biome data
    uniform sampler2D controlTexture;
    
    // Scaling factors for textures (to avoid tiling artifacts)
    uniform float uTextureScale;
    
    // Height and slope thresholds for texture transitions
    uniform float uBeachThreshold;
    
    // Smoothness of the transitions
    uniform float uTransitionSmoothness;
    
    // Debug visualization mode (0=height only, 1=biome only, 2=full textures, 3=raw heightmap, 4=island outline)
    uniform int uVisualizationMode;
    
    // Varyings from vertex shader
    varying vec2 vUv;
    varying vec3 vPosition;
    varying vec3 vNormal;
    varying float vHeight;
    varying float vSlope;
    
    // Shadow mapping functions
    float getShadow(sampler2D shadowMap, vec2 shadowMapSize, float shadowBias, float shadowRadius, vec4 shadowCoord) {
      shadowCoord.xyz /= shadowCoord.w;
      shadowCoord.z += shadowBias;
      
      // Check if position is in shadow
      bvec4 inFrustumVec = bvec4(shadowCoord.x >= 0.0, shadowCoord.x <= 1.0, shadowCoord.y >= 0.0, shadowCoord.y <= 1.0);
      bool inFrustum = all(inFrustumVec);
      
      bvec2 frustumTestVec = bvec2(inFrustum, shadowCoord.z <= 1.0);
      bool frustumTest = all(frustumTestVec);
      
      if (frustumTest) {
        #ifdef SHADOWMAP_TYPE_PCF
          vec2 texelSize = vec2(1.0) / shadowMapSize;
          
          float shadow = 0.0;
          
          // PCF filtering for soft shadows
          float dx0 = -texelSize.x * shadowRadius;
          float dy0 = -texelSize.y * shadowRadius;
          float dx1 = texelSize.x * shadowRadius;
          float dy1 = texelSize.y * shadowRadius;
          
          shadow += step(shadowCoord.z, unpackRGBAToDepth(texture2D(shadowMap, shadowCoord.xy + vec2(dx0, dy0))));
          shadow += step(shadowCoord.z, unpackRGBAToDepth(texture2D(shadowMap, shadowCoord.xy + vec2(0.0, dy0))));
          shadow += step(shadowCoord.z, unpackRGBAToDepth(texture2D(shadowMap, shadowCoord.xy + vec2(dx1, dy0))));
          shadow += step(shadowCoord.z, unpackRGBAToDepth(texture2D(shadowMap, shadowCoord.xy + vec2(dx0, 0.0))));
          shadow += step(shadowCoord.z, unpackRGBAToDepth(texture2D(shadowMap, shadowCoord.xy)));
          shadow += step(shadowCoord.z, unpackRGBAToDepth(texture2D(shadowMap, shadowCoord.xy + vec2(dx1, 0.0))));
          shadow += step(shadowCoord.z, unpackRGBAToDepth(texture2D(shadowMap, shadowCoord.xy + vec2(dx0, dy1))));
          shadow += step(shadowCoord.z, unpackRGBAToDepth(texture2D(shadowMap, shadowCoord.xy + vec2(0.0, dy1))));
          shadow += step(shadowCoord.z, unpackRGBAToDepth(texture2D(shadowMap, shadowCoord.xy + vec2(dx1, dy1))));
          
          shadow /= 9.0;
        #else
          float shadow = step(shadowCoord.z, unpackRGBAToDepth(texture2D(shadowMap, shadowCoord.xy)));
        #endif
        
        return shadow;
      }
      
      return 1.0;
    }
    
    // Function to calculate blend weights based on height and slope
    vec2 calculateBlendWeights() {
      // Get biome data from control texture
      vec4 biomeData = texture2D(controlTexture, vUv);
      float redValue = biomeData.r;
      
      // Initialize weights
      float beachWeight = 0.0;
      float grassWeight = 0.0;
      
      // Check if we're in beach territory based on the red channel
      // Beach should have redValue between 0.5 and 0.75
      if (redValue > 0.3 && redValue < 0.75) {
        beachWeight = 1.0;
      } 
      // If we're not beach and not water (redValue < 0.75), then we're grass
      else if (redValue < 0.3) {
        // Grass or forest based on green/blue
        grassWeight = 1.0;
        
        // Use green and blue channels to differentiate between grass and forest
        if (biomeData.g > 0.0 || biomeData.b > 0.0) {
          // Adjust weights based on biome data
          grassWeight = biomeData.g / (biomeData.g + biomeData.b + 0.01);
        }
      }
      
      // Slope influence (reduce grass on very steep slopes)
      float slopeInfluence = smoothstep(0.2, 0.5, vSlope);
      grassWeight *= (1.0 - slopeInfluence * 0.8);
      
      // Normalize weights to ensure they sum to 1.0
      float totalWeight = beachWeight + grassWeight;
      if (totalWeight > 0.001) {
        beachWeight /= totalWeight;
        grassWeight /= totalWeight;
      } else {
        // If all weights are 0 or very small, fall back to height-based weights
        if (vHeight < uBeachThreshold) {
          beachWeight = 1.0;
          grassWeight = 0.0;
        } else {
          beachWeight = 0.0;
          grassWeight = 1.0;
        }
      }
      
      return vec2(beachWeight, grassWeight);
    }
    
    // Function to visualize height only
    vec4 visualizeHeight() {
      // Get control texture data
      vec4 controlData = texture2D(controlTexture, vUv);
      float redValue = controlData.r;
      
      // Match values from IslandGenerator.js
      // Deep water: red = 255
      // Shallow water: red = 200
      // Beach: red = 180
      
      if (redValue > 0.9) { // Deep water (value ~1.0)
        float depth = clamp(1.0 - vHeight * 5.0, 0.0, 1.0);
        return vec4(
          0.0/255.0, 
          20.0/255.0, 
          120.0/255.0,
          0.8
        );
      } else if (redValue > 0.75) { // Shallow water (value ~0.78)
        return vec4(
          30.0/255.0, 
          100.0/255.0, 
          200.0/255.0,
          0.8
        );
      } else if (redValue > 0.5) { // Beach (value ~0.7)
        return vec4(0.95, 0.85, 0.65, 1.0);
      } else {
        // All other terrain - green
        return vec4(0.2, 0.8, 0.2, 1.0);
      }
    }
    
    // Function to visualize biomes
    vec4 visualizeBiome() {
      vec4 biomeData = texture2D(controlTexture, vUv);
      float redValue = biomeData.r;
      
      // Deep water
      if (redValue > 0.9) {
        float depth = clamp(1.0 - vHeight * 5.0, 0.0, 1.0);
        vec3 waterColor = mix(
          vec3(30.0/255.0, 100.0/255.0, 200.0/255.0), // Shallow water color
          vec3(0.0/255.0, 20.0/255.0, 120.0/255.0),   // Deep water color
          depth
        );
        return vec4(waterColor, 0.8);
      }
      
      // Shallow water
      if (redValue > 0.75) {
        return vec4(30.0/255.0, 100.0/255.0, 200.0/255.0, 0.8);
      }
      
      // Beach
      if (redValue > 0.5) {
        return vec4(1.0, 0.9, 0.6, 1.0);
      }
      
      // Grassland/Forest (combined since we removed rock/snow)
      if (biomeData.g > 0.2 || biomeData.b > 0.2) {
        float forestInfluence = biomeData.b / (biomeData.g + biomeData.b);
        return mix(
          vec4(0.3, 0.8, 0.3, 1.0), // Grass color
          vec4(0.0, 0.5, 0.0, 1.0),  // Forest color
          forestInfluence
        );
      }
      
      // Default to beach if no strong biome influence
      return vec4(1.0, 0.9, 0.6, 1.0);
    }
    
    // Function to visualize raw heightmap
    vec4 visualizeRawHeight() {
      // Simple grayscale based purely on height value
      float value = clamp(vHeight, 0.0, 1.0);
      return vec4(value, value, value, 1.0);
    }
    
    // Function to visualize only the island outline
    vec4 visualizeIslandOutline() {
      // Get biome data to determine land vs. water
      vec4 controlData = texture2D(controlTexture, vUv);
      bool isWater = controlData.r > 0.75;  // Matches water threshold (shallow + deep water)
      
      // Check if we're near a boundary between land and water
      float outlineThickness = 0.005; // Thickness of the outline
      bool isOutline = false;
      
      // Sample neighboring points to detect edges
      for (float dx = -outlineThickness; dx <= outlineThickness; dx += outlineThickness) {
        for (float dy = -outlineThickness; dy <= outlineThickness; dy += outlineThickness) {
          if (dx == 0.0 && dy == 0.0) continue;
          
          vec2 sampleUv = vUv + vec2(dx, dy);
          vec4 sampleData = texture2D(controlTexture, sampleUv);
          bool sampleIsWater = sampleData.r > 0.75;  // Use same threshold as above
          
          // If current pixel and sample are different (one is water, one is land)
          // then we're at an edge
          if (isWater != sampleIsWater) {
            isOutline = true;
            break;
          }
        }
      }
      
      // Render the outline and basic terrain
      if (isOutline) {
        // Black outline
        return vec4(0.0, 0.0, 0.0, 1.0);
      } else if (isWater) {
        // Very light blue for water
        return vec4(0.9, 0.9, 1.0, 0.8);
      } else {
        // Very light tan for land
        return vec4(0.98, 0.96, 0.86, 1.0);
      }
    }
    
    void main() {
      // GLSL-safe check for valid UV coordinates - no "null" in GLSL
      // Check if UV coordinates are out of a valid range instead
      if (vUv.x < -1000.0 || vUv.y < -1000.0) {
        gl_FragColor = vec4(1.0, 0.0, 1.0, 1.0); // Pink for error
        return;
      }
      
      // Calculate shadow factor
      float shadowFactor = 1.0;
      
      #ifdef USE_SHADOWMAP
        #if NUM_DIR_LIGHT_SHADOWS > 0
          #pragma unroll_loop_start
          for (int i = 0; i < NUM_DIR_LIGHT_SHADOWS; i++) {
            shadowFactor *= getShadow(
              directionalShadowMap[i],
              shadowMapSize[i],
              shadowBias[i],
              shadowRadius[i],
              vDirectionalShadowCoord[i]
            );
          }
          #pragma unroll_loop_end
        #endif
      #endif
      
      // Get control texture data for biome information
      vec4 controlData = texture2D(controlTexture, vUv);
      float redValue = controlData.r;
      
      // Handle water specially - use a blue color for water
      // Water is indicated by high red channel value in control texture
      // Deep water: red > 0.9, Shallow water: red > 0.75
      if (redValue > 0.75 && uVisualizationMode != 3 && uVisualizationMode != 4) { // Skip for raw heightmap and outline mode
        // Water color from dark blue to light blue based on depth
        float depth = 1.0 - vHeight * 5.0; // Use height to determine water depth
        depth = clamp(depth, 0.0, 1.0);
        
        vec3 waterColor;
        
        // Deep vs shallow water
        if (redValue > 0.9) {
          waterColor = mix(
            vec3(30.0/255.0, 100.0/255.0, 200.0/255.0), // Shallow water color
            vec3(0.0/255.0, 20.0/255.0, 120.0/255.0),   // Deep water color
            depth
          );
        } else {
          // Shallow water - lighter blue
          waterColor = vec3(30.0/255.0, 100.0/255.0, 200.0/255.0);
        }
        
        // Apply shadows to water (slightly less intense)
        waterColor = mix(waterColor, waterColor * 0.7, 1.0 - min(shadowFactor + 0.3, 1.0));
        
        gl_FragColor = vec4(waterColor, 0.8); // Semi-transparent water
        return;
      }
      
      // Choose visualization mode
      vec4 baseColor;
      
      if (uVisualizationMode == 0) {
        // Height only visualization
        baseColor = visualizeHeight();
      } else if (uVisualizationMode == 1) {
        // Biome visualization
        baseColor = visualizeBiome();
      } else if (uVisualizationMode == 3) {
        // Raw heightmap visualization
        baseColor = visualizeRawHeight();
      } else if (uVisualizationMode == 4) {
        // Island outline visualization
        baseColor = visualizeIslandOutline();
      } else {
        // Default mode: Full texture blending
        // Scale UVs to avoid obvious tiling
        vec2 scaledUV = vUv * uTextureScale;
        
        // Sample textures at different scales for variety
        vec4 sandColor = texture2D(sandTexture, scaledUV);
        vec4 grassColor = texture2D(grassTexture, scaledUV * 0.9);
        
        // Calculate blend weights for land textures
        vec2 blendWeights = calculateBlendWeights();
        
        // Blend textures
        baseColor = 
          sandColor * blendWeights.x +
          grassColor * blendWeights.y;
        
        // Ensure full opacity for land
        baseColor.a = 1.0;
      }
      
      // Apply shadow to all non-water surfaces
      vec3 finalColor = baseColor.rgb * shadowFactor;
      
      gl_FragColor = vec4(finalColor, baseColor.a);
    }
  `,
  
  // Default uniform values
  defaultUniforms: {
    uTextureScale: 20.0,
    uBeachThreshold: 0.3,
    uTransitionSmoothness: 0.1,
    uVisualizationMode: 2 // Full textures by default
  }
};